/**
 * Canonical /blog/ index filtering helpers.
 *
 * Source of truth: the `target_audience` and `topic_cluster` columns on
 * `blog_posts_renderable`. No regex inference, no title/excerpt text
 * matching for audience or topic classification.
 */

export type CanonicalAudience = "agencies" | "employers" | "temps" | "general";

export const CANONICAL_AUDIENCES: CanonicalAudience[] = [
  "agencies",
  "employers",
  "temps",
  "general",
];

export const AUDIENCE_LABELS: Record<CanonicalAudience, string> = {
  agencies: "Agencies",
  employers: "Employers",
  temps: "Temps",
  general: "General",
};

/** Pill rail shown next to the search input. */
export const AUDIENCE_PILLS: { value: CanonicalAudience | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "agencies", label: "Agencies" },
  { value: "employers", label: "Employers" },
  { value: "temps", label: "Temps" },
];

/**
 * Popular-topic slug → approved `topic_cluster` values.
 *
 * Mappings verified against current production posts so each pill returns
 * topically relevant articles only. See
 * docs/blog-topical-authority/blog-index-filter-fix-report.md.
 */
export const POPULAR_TOPIC_CLUSTERS = {
  "worker-availability": ["temp_schedule_availability"],
  "timesheet-approvals": ["employer_budget_reporting"],
  "agency-compliance": [
    "agency_worker_management",
    "employer_compliance_integrations",
  ],
  "shift-fulfilment": [
    "agency_shift_matching",
    "employer_shift_management",
    "temp_shift_discovery",
  ],
  "employer-bookings": ["agency_employer_portal", "employer_shift_management"],
} as const;

export type PopularTopicSlug = keyof typeof POPULAR_TOPIC_CLUSTERS;

/**
 * Visible labels for the popular-topic pills. Labels are chosen so they
 * honestly represent the mapped cluster set (e.g. `timesheet-approvals`
 * also reports on permanent vs. temporary cost analysis, so the label
 * reflects "Timesheets & reporting").
 */
export const POPULAR_TOPIC_LABELS: Record<PopularTopicSlug, string> = {
  "worker-availability": "Worker availability",
  "timesheet-approvals": "Timesheets & reporting",
  "agency-compliance": "Agency compliance",
  "shift-fulfilment": "Shift fulfilment",
  "employer-bookings": "Employer bookings",
};

export const POPULAR_TOPIC_SLUGS = Object.keys(
  POPULAR_TOPIC_CLUSTERS,
) as PopularTopicSlug[];

export function isPopularTopicSlug(value: unknown): value is PopularTopicSlug {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(POPULAR_TOPIC_CLUSTERS, value)
  );
}

export function audienceFromParam(
  value: string | null | undefined,
): CanonicalAudience | "all" {
  if (!value) return "all";
  const v = value.toLowerCase();
  if (v === "all" || v === "agencies" || v === "employers" || v === "temps") {
    return v;
  }
  return "all";
}

export interface FilterablePost {
  title: string;
  excerpt: string | null;
  target_audience: string | null;
  topic_cluster: string | null;
}

export function postMatchesAudience(
  post: Pick<FilterablePost, "target_audience">,
  audience: CanonicalAudience | "all",
): boolean {
  if (audience === "all") return true;
  return post.target_audience === audience;
}

export function postMatchesPopularTopic(
  post: Pick<FilterablePost, "topic_cluster">,
  topic: PopularTopicSlug | null,
): boolean {
  if (!topic) return true;
  const clusters = POPULAR_TOPIC_CLUSTERS[topic] as readonly string[];
  return !!post.topic_cluster && clusters.includes(post.topic_cluster);
}

export function postMatchesSearch(
  post: Pick<FilterablePost, "title" | "excerpt">,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const title = post.title.toLowerCase();
  const excerpt = (post.excerpt ?? "").toLowerCase();
  return title.includes(q) || excerpt.includes(q);
}

/**
 * Display label for an audience badge.
 *
 * Unexpected / NULL values are deliberately surfaced as "Uncategorised"
 * outside production so classification gaps stay visible. In production
 * we still want a stable label, so an unknown value falls back to
 * "General".
 */
export function audienceBadgeLabel(value: string | null | undefined): string {
  if (value === "agencies" || value === "employers" || value === "temps" || value === "general") {
    return AUDIENCE_LABELS[value];
  }
  if (typeof import.meta !== "undefined" && (import.meta as { env?: { PROD?: boolean } }).env?.PROD) {
    return AUDIENCE_LABELS.general;
  }
  return "Uncategorised";
}